// citationNumbering.js
// Calculate citation numbers for references

/**
 * Calculate citation numbers based on document order
 * Each unique localId gets a number based on its first appearance
 *
 * @param {Array<{uid: string, from: number}>} orderedMarks - Reference marks in document order
 * @param {Map<string, Array<{localId: string}>>} referencesByMark - Map(markId -> references)
 * @returns {Map<string, number>} Map(localId -> citation number)
 */
export function calculateCitationNumbers(orderedMarks, referencesByMark) {
    const numbersByLocalId = new Map();
    let nextNumber = 1;

    // Process marks in document order
    orderedMarks.forEach(({uid}) => {
        const references = referencesByMark.get(uid);
        if (!references) return;

        // Process references within each mark
        references.forEach((ref) => {
            const localId = ref.localId;
            if (!localId) return;

            // Assign number on first appearance only
            if (!numbersByLocalId.has(localId)) {
                numbersByLocalId.set(localId, nextNumber);
                nextNumber++;
            }
        });
    });

    return numbersByLocalId;
}

/**
 * Get citation numbers for a specific reference mark
 * @param {string} markId - Reference mark UID
 * @param {Map<string, Array<{localId: string}>>} referencesByMark - References by mark
 * @param {Map<string, number>} numbersByLocalId - Citation numbers by localId
 * @returns {Array<number>} Sorted array of citation numbers
 */
export function getCitationNumbersForMark(markId, referencesByMark, numbersByLocalId) {
    const references = referencesByMark.get(markId);
    if (!references || references.length === 0) return [];

    // Get citation numbers for all references in this mark
    const numbers = new Set();
    references.forEach(ref => {
        if (ref.localId) {
            const num = numbersByLocalId.get(ref.localId);
            if (num != null) {
                numbers.add(num);
            }
        }
    });

    // Return sorted array
    return Array.from(numbers).sort((a, b) => a - b);
}

/**
 * Create a hash of citation state for change detection
 * @param {Map<string, number>} numbersByLocalId - Citation numbers
 * @param {Map<string, Array>} referencesByMark - References by mark
 * @returns {string} Hash string
 */
export function hashCitationState(numbersByLocalId, referencesByMark) {
    const numberEntries = Array.from(numbersByLocalId.entries()).sort((a, b) =>
        a[0].localeCompare(b[0])
    );

    // Include reference contents (localIds) per mark to ensure decoration updates
    // when references are added/removed without changing mark IDs.
    const markEntries = Array.from(referencesByMark.entries())
        .map(([markId, refs]) => {
            const localIds = (refs || [])
                .map(r => r?.localId)
                .filter(Boolean)
                .map(String)
                .sort();
            return [String(markId), localIds];
        })
        .sort((a, b) => a[0].localeCompare(b[0]));

    return JSON.stringify({
        numbers: numberEntries,
        marks: markEntries
    });
}

/**
 * Get the count of references for a specific reference mark
 * @param {string} markId - Reference mark UID
 * @param {Map<string, Array>} referencesByMark - References by mark
 * @returns {number} Count of references for this mark
 */
export function getReferenceCountForMark(markId, referencesByMark) {
    const references = referencesByMark.get(markId);
    return references ? references.length : 0;
}

